import useApi from "@/hooks/useApi";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Invitation } from "./types";
import {
  TableBase, PrimaryBlueButton, SecondaryGrayButton, IconPlus, Modal, ModalHeader, ModalBody, ModalFooter
} from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import InvitationModal from "./InvitationModal";
import { IconTrash } from "@velaro/velaro-component-lib";
import { FormGroup } from "../Workflows/RightAside/RightAsideHelpers";
import IconEditPencil2 from "@/icons/IconEditPencil2";
import useTeams from "@/hooks/useTeams";
import { getExpressionLabel } from "@/components/Expressions/types";
import Tooltip from "@/components/Tooltip";

const columns = [
  {
    label: "Name",
    accessor: "name"
  },
  {
    label: "Description",
    accessor: "description"
  },
  {
    label: "Type",
    accessor: "type"
  },
  {
    label: "Rule",
    accessor: "rule"
  },
  {
    label: "",
    accessor: "edit",
    disableSort: true
  }
];

export default function Index() {
  const [invitations, setInvitations] = useState<Invitation[] | undefined>();
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | undefined>();
  const [invitationToDelete, setInvitationToDelete] = useState<number | null>(null);
  const { getTeamNameById } = useTeams();
  const api = useApi();

  useEffect(() => {
    async function fetch() {
      const response = await api.messaging.get("Invitations/List");
      const data = (await response.json()) as Invitation[];
      setInvitations(data);
      setLoading(false);
    }
    fetch();
  }, [api.messaging]);

  const remove = useCallback(
    async (id: number) => {
      setLoading(true);
      const response = await api.messaging.delete(`Invitations/${id}`);
      if (response.ok) {
        setInvitations(invitations?.filter((i) => i.id !== id));
      }
      setLoading(false);
    },
    [api.messaging, invitations]
  );

  const tableData = useMemo(() => {
    if (!invitations) {
      return [];
    }

    function renderActionBar(id: number) {
      return (
        <div className="flex flex-row gap-4 items-center text-slate-400">
          <Tooltip text="Edit" direction="top" minWidth="50px">
            <div
              className="hover:cursor-pointer hover:text-primary"
              onClick={() => setSelectedId(id)}
            >
              <IconEditPencil2 />
            </div>
          </Tooltip>
          <Tooltip text="Delete" direction="top" minWidth="60px">
            <div
              className="hover:cursor-pointer hover:text-primary"
              onClick={() => setInvitationToDelete(id)}
            >
              <IconTrash />
            </div>
          </Tooltip>
        </div>
      );
    }

    return invitations.map((invitation) => ({
      ...invitation,
      rule: getExpressionLabel(invitation.triggerExpression, getTeamNameById),
      edit: renderActionBar(invitation.id!)
    }));
  }, [getTeamNameById, invitations]);

  if (loading || !invitations) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <FormGroup>
        <PrimaryBlueButton
          onClick={() => setSelectedId(0)}
          label="Add"
          prefixIcon={<IconPlus />}
        />
      </FormGroup>
      <FormGroup>
        <div className="grow overflow-auto h-full w-full">
          {invitations.length === 0 && <div>You have no invitations</div>}
          {invitations.length > 0 && (
            <TableBase columns={columns} data={tableData} />
          )}
        </div>
      </FormGroup>
      {selectedId !== undefined && (
        <InvitationModal
          onClose={() => setSelectedId(undefined)}
          onUpdate={(invitation) => {
            setSelectedId(undefined);
            const index = invitations.findIndex((i) => i.id === invitation.id);
            if (index === -1) {
              setInvitations([...invitations, invitation]);
              return;
            }
            const clone = [...invitations];
            clone[index] = invitation;
            setInvitations(clone);
          }}
          invitation={invitations.find((i) => i.id === selectedId)}
        />
      )}
      <Modal show={invitationToDelete !== null}>
        <ModalHeader
          title="Confirm Delete"
          onClose={() => setInvitationToDelete(null)}
        />
        <ModalBody>
          <div className="m-4">
            Are you sure you want to delete this invitation?
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="mx-4 my-2 flex justify-between items-center">
            <SecondaryGrayButton
              label="Cancel"
              onClick={() => setInvitationToDelete(null)}
            />
            <PrimaryBlueButton
              label="Delete"
              onClick={() => {
                if (invitationToDelete !== null) {
                  remove(invitationToDelete);
                }
                setInvitationToDelete(null);
              }}
            />
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
}
